import React, { useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import PageTitle from '../../components/PageTitle';
import MUIModal from '../../components/MuiModal';
import Loader from '../../common/Loader';
import KpiCard from './shared/KpiCard';
import PillFilters from './shared/PillFilters';
import FilterBar from './shared/FilterBar';
import { PILL_KEYS, buildPillFilter, getDefaultDateRange } from './shared/constants';
import useAppointmentSearch from './shared/useAppointmentSearch';
import useAppointmentDashboard from './shared/useAppointmentDashboard';
import useAbdSettings from './shared/useAbdSettings';
import { buildAppointmentColumns } from './shared/columns';
import ExpandableAppointmentsTable from './shared/ExpandableAppointmentsTable';
import ExportButton from './shared/ExportButton';
import AppointmentDelivery from '../B2BOrder/AddAppDelivery';
import MarkMissedModal from './modals/MarkMissedModal';
import DocumentsModal from '../B2BOrder/DocumentsModal';
import CopyIcon from '../../common/CopyIcon';

// Pending-scoped pills exclude delivered / RTO-delivered / cancelled / lost /
// damaged shipments, so they never sum to "All". Said on the pill itself.
const PENDING_NOTE = 'Still-pending shipments only. Delivered, RTO-delivered, cancelled, lost or damaged shipments are excluded.';
const PILLS = [
    { key: PILL_KEYS.ALL, label: 'All', info: 'All ABD shipments registered in the date range, any status.' },
    { key: PILL_KEYS.ACTIVE, label: 'Active', info: `Has a scheduled appointment. ${PENDING_NOTE}` },
    { key: PILL_KEYS.MISS_1, label: '1 Miss', info: `Exactly 1 missed appointment. ${PENDING_NOTE}` },
    { key: PILL_KEYS.MISS_2, label: '2 Misses', info: `Exactly 2 missed appointments. ${PENDING_NOTE}` },
    { key: PILL_KEYS.MISS_3_PLUS, label: '3+ Misses', danger: true, info: `3 or more missed appointments. ${PENDING_NOTE}` },
    { key: PILL_KEYS.NEEDS_SCHEDULING, label: 'Needs Scheduling', info: `No next appointment scheduled yet. ${PENDING_NOTE}` },
    { key: PILL_KEYS.DELIVERED, label: 'Delivered', info: 'Shipments delivered, with or without earlier misses.' },
];

export default function AllAppointments() {
    const def = getDefaultDateRange();
    const [filters, setFilters] = useState({ fromDate: def.fromDate, toDate: def.toDate });
    const [pill, setPill] = useState(PILL_KEYS.ALL);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(50);
    const [reload, setReload] = useState(0);

    const [scheduleRow, setScheduleRow] = useState(null);
    const [editRow, setEditRow] = useState(null);
    const [missRow, setMissRow] = useState(null);
    const [apptDocsRow, setApptDocsRow] = useState(null);

    const extraAndFilter = buildPillFilter(pill);
    const { data: searchData, isFetching } = useAppointmentSearch({
        filters,
        extraAndFilter,
        page,
        sizePerPage,
        reload,
    });
    const { settings: abdSettings, error: abdSettingsError } = useAbdSettings();
    const { data: kpi } = useAppointmentDashboard({
        filters, reload, include: ['buckets', 'summary'],
    });

    const counts = {
        [PILL_KEYS.ALL]: kpi.total,
        [PILL_KEYS.ACTIVE]: kpi.active,
        [PILL_KEYS.MISS_1]: kpi.miss1,
        [PILL_KEYS.MISS_2]: kpi.miss2,
        [PILL_KEYS.MISS_3_PLUS]: kpi.miss3plus,
        [PILL_KEYS.NEEDS_SCHEDULING]: kpi.needsScheduling,
        [PILL_KEYS.DELIVERED]: kpi.delivered,
    };

    const triggerReload = () => setReload((r) => r + 1);

    const columns = buildAppointmentColumns({
        onSchedule: (row) => setScheduleRow(row),
        onEdit: (row) => setEditRow(row),
        abdSettings,
        abdSettingsError,
        onMarkMissed: (row) => setMissRow(row),
        onAddDocs: (row) => setApptDocsRow(row),
    });

    return (
        <>
            <PageTitle>All Appointments</PageTitle>
            {isFetching && <Loader />}

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={2.4}>
                    <KpiCard color='blue' label='Total Appointment Shipments' value={kpi.total}
                        sub='In selected date range' icon={<EventNoteIcon />} selected
                        info='ABD shipments whose AWB was registered in the selected date range, any current status.' />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard color='green' label='Active Appointments' value={kpi.active}
                        sub='Currently scheduled' icon={<EventAvailableIcon />}
                        info={`Shipments with a scheduled appointment. ${PENDING_NOTE}`} />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard color='red' label='Needs Scheduling' value={kpi.needsScheduling}
                        sub='Waiting for next appointment' icon={<ErrorOutlineIcon />}
                        info={`Shipments with no next appointment scheduled. ${PENDING_NOTE}`} />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard color='teal' label='Delivered' value={kpi.delivered}
                        sub='Successfully delivered' icon={<DoneAllIcon />}
                        info='Shipments in DELIVERED status, with or without earlier missed appointments.' />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard color='amber' label='Prozo Fault Shipments' value={kpi.prozoFault}
                        sub='Still open · in selected range' icon={<ReportProblemIcon />}
                        info={`Shipments with at least one miss attributed to Prozo. ${PENDING_NOTE} For missed-appointment counts by attribution see ABD Analytics.`} />
                </Grid>
            </Grid>

            <PillFilters pills={PILLS} value={pill} onChange={(k) => {
                setPill(k);
                setPage(0);
                // missCategory adds a "still-pending" exclusion that contradicts
                // delivered-only pills — auto-clear so the grid shows results
                // instead of an empty page.
                if (k === PILL_KEYS.DELIVERED && filters.missCategory) {
                    setFilters((f) => ({ ...f, missCategory: '' }));
                }
            }} counts={counts} />

            <FilterBar
                show={['merchant', 'courier', 'awb', 'city', 'attempts', 'missCategory', 'documents', 'dateRange']}
                onApply={(f) => { setFilters(f); setPage(0); triggerReload(); }}
                onReset={() => { setFilters({ fromDate: def.fromDate, toDate: def.toDate }); setPage(0); triggerReload(); }}
            />

            {/* Each button posts the SAME body shape as /order/search, just with a
                different extraAndFilter so the CSV mirrors that view exactly:
                  - "Export All"             → current filters + ALL pill
                  - "Download Missed"        → current filters + force >=1 history miss
                  - "Download Needs Sched"   → current filters + needsScheduling pill
                Server returns CSV stream with V2 fields. Styling matches mockup:
                neutral outlined for "Export All", solid orange for "Missed",
                solid blue for "Needs Scheduling". */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5,
                pt: 1.5, mt: -0.5, borderTop: '1px solid #eee',
            }}>
                <Typography
                    variant='caption' fontWeight={600}
                    sx={{ color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}
                >Download:</Typography>
                <ExportButton
                    label='Export All (Excel)'
                    filters={filters}
                    extraAndFilter={buildPillFilter(pill)}
                    fileName='ABD_All_Appointments'
                />
                <ExportButton
                    label='Download Missed Shipments (1+ miss, any status)'
                    filters={filters}
                    extraAndFilter={[{ field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 1 }]}
                    fileName='ABD_Missed_Appointments'
                    buttonStyle='contained'
                    bgColor='#e65100'
                />
                <ExportButton
                    label='Download Needs Scheduling'
                    filters={filters}
                    extraAndFilter={buildPillFilter(PILL_KEYS.NEEDS_SCHEDULING)}
                    fileName='ABD_Needs_Scheduling'
                    buttonStyle='contained'
                    bgColor='#1565c0'
                />
                <Typography variant='caption' sx={{ color: '#aaa', ml: 'auto', fontSize: 10 }}>
                    Downloads reflect current filters
                </Typography>
            </Box>

            <ExpandableAppointmentsTable
                columns={columns}
                rows={searchData?.data?.length > 0 ? searchData.data : []}
                page={page}
                sizePerPage={sizePerPage}
                setPage={setPage}
                setSizePerPage={setSizePerPage}
                rowCount={Number.MAX_VALUE}
                getRowId={(row) => row.id}
            />

            <MUIModal
                open={!!scheduleRow}
                handleClose={() => setScheduleRow(null)}
                maxWidth='lg'
                hideClose
                title={
                    <Typography variant='subtitle1' sx={{ display: 'flex', alignItems: 'center' }}>
                        {scheduleRow?.appointmentDelivery ? 'Reschedule' : 'Schedule'} Appointment — AWB:{' '}
                        <Typography component='span' color='error' fontWeight='bold'>{scheduleRow?.awb_number}</Typography>
                        <CopyIcon text={scheduleRow?.awb_number} />
                    </Typography>
                }
            >
                <AppointmentDelivery
                    rowData={scheduleRow}
                    setDelConfirm={() => setScheduleRow(null)}
                    setReload={triggerReload}
                />
            </MUIModal>

            <MUIModal
                open={!!editRow}
                handleClose={() => setEditRow(null)}
                maxWidth='lg'
                hideClose
                title={
                    <Typography variant='subtitle1' sx={{ display: 'flex', alignItems: 'center' }}>
                        Edit Appointment — AWB:{' '}
                        <Typography component='span' color='error' fontWeight='bold'>{editRow?.awb_number}</Typography>
                        <CopyIcon text={editRow?.awb_number} />
                    </Typography>
                }
            >
                <AppointmentDelivery
                    rowData={editRow}
                    isEditMode
                    abdSettings={abdSettings}
                    setDelConfirm={() => setEditRow(null)}
                    setReload={triggerReload}
                />
            </MUIModal>

            <MUIModal
                open={!!missRow}
                handleClose={() => setMissRow(null)}
                maxWidth='md'
                title={
                    <Typography variant='subtitle1' sx={{ display: 'flex', alignItems: 'center' }}>
                        Mark as Missed — AWB:{' '}
                        <Typography component='span' color='error' fontWeight='bold'>{missRow?.awb_number}</Typography>
                        <CopyIcon text={missRow?.awb_number} />
                    </Typography>
                }
            >
                <MarkMissedModal
                    rowData={missRow}
                    onClose={() => setMissRow(null)}
                    onDone={triggerReload}
                    onScheduleNext={(row) => setScheduleRow(row)}
                />
            </MUIModal>

            <MUIModal
                open={!!apptDocsRow}
                handleClose={() => setApptDocsRow(null)}
                maxWidth='md'
                title={
                    <Typography variant='subtitle1' sx={{ display: 'flex', alignItems: 'center' }}>
                        Add Documents — AWB:{' '}
                        <Typography component='span' color='error' fontWeight='bold'>{apptDocsRow?.awb_number}</Typography>
                        <CopyIcon text={apptDocsRow?.awb_number} />
                    </Typography>
                }
                hideClose
            >
                <DocumentsModal
                    rowData={apptDocsRow}
                    setOpen={() => setApptDocsRow(null)}
                    setReload={triggerReload}
                />
            </MUIModal>

        </>
    );
}
